import { useState } from 'react'
import { FileText, Link2, StickyNote, Download } from 'lucide-react'

function getExpiryStatus(expiryDate) {
  if (!expiryDate) return null
  const now = new Date()
  const expiry = new Date(expiryDate)
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24))
  if (daysUntilExpiry < 0) return 'expired'
  if (daysUntilExpiry <= 30) return 'expiring_soon'
  return 'active'
}

function TypeIcon({ type }) {
  if (type === 'file') return <FileText className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
  if (type === 'link') return <Link2 className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
  return <StickyNote className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
}

export default function EvidenceItem({ evidence, onDelete, onDownload }) {
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [loadingUrl, setLoadingUrl] = useState(false)

  const expiryStatus = getExpiryStatus(evidence.expiry_date)
  const isExpired = evidence.status === 'expired' || expiryStatus === 'expired'

  async function handleDownload() {
    if (evidence.type !== 'file') return
    setLoadingUrl(true)
    try {
      const url = await onDownload(evidence.id)
      const link = document.createElement('a')
      link.href = url
      link.download = evidence.title || 'evidence'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      console.error('Failed to get download URL:', err)
    }
    setLoadingUrl(false)
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      await onDelete(evidence.id)
    } catch (err) {
      console.error('Failed to delete evidence:', err)
    }
    setDeleting(false)
    setConfirmDelete(false)
  }

  return (
    <div>
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <TypeIcon type={evidence.type} />
          <div className="flex-1 min-w-0">

            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="text-sm font-medium text-white">{evidence.title}</p>

              {isExpired ? (
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/10 text-red-400 border border-red-500/20">
                  Expired
                </span>
              ) : expiryStatus === 'expiring_soon' ? (
                <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                  Expiring Soon
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-400 border border-green-500/20">
                  Active
                </span>
              )}
            </div>

            <p className="text-xs text-gray-500 mb-1">
              {evidence.type === 'file' && 'File · '}
              {evidence.type === 'link' && 'Link · '}
              {evidence.type === 'note' && 'Note · '}
              Uploaded by {evidence.profiles?.full_name || 'Unknown'} ·{' '}
              {new Date(evidence.upload_date).toLocaleDateString()}
            </p>

            {evidence.expiry_date && (
              <p className={`text-xs ${
                expiryStatus === 'expired' ? 'text-red-400' :
                expiryStatus === 'expiring_soon' ? 'text-yellow-400' :
                'text-gray-500'
              }`}>
                {expiryStatus === 'expired' ? 'Expired' : 'Expires'}{' '}
                {new Date(evidence.expiry_date).toLocaleDateString()}
              </p>
            )}

            {evidence.type === 'link' && evidence.url && (
              <a
                href={evidence.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors mt-1 block truncate"
              >
                {evidence.url}
              </a>
            )}

            {evidence.type === 'note' && evidence.note && (
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                {evidence.note}
              </p>
            )}

          </div>
        </div>

        <div className="flex items-center gap-2 ml-3 shrink-0">
          {evidence.type === 'file' && (
            <button
              onClick={handleDownload}
              disabled={loadingUrl}
              className="
                inline-flex items-center gap-1.5
                text-xs text-gray-400
                hover:text-white
                disabled:opacity-50
                transition-colors
              "
            >
              <Download className="w-3.5 h-3.5" />
              {loadingUrl ? 'Loading...' : 'Download'}
            </button>
          )}

          {onDelete && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              Delete
            </button>
          )}

          {onDelete && confirmDelete && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Sure?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-gray-500 hover:text-white transition-colors"
              >
                No
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}